import { useCallback, useEffect, useMemo, useState } from "react";

import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { create } from "zustand";

import { useDebounce } from "Utilities/Hooks/useDebounce";
import { useLocalStorage } from "Utilities/Hooks/useLocalStorage";

import { useActiveSignups } from "./API/queryHooks";

const useSignupsAndEventsStore = create((set) => ({
  latestSignupsVisited: [],
  setLatestSignupsVisited: (newLatestSignupsVisited) =>
    set({ latestSignupsVisited: newLatestSignupsVisited }),
  isInitRef: { current: false },
  searchValue: "",
  setSearchValue: (newSearchValue) => set({ searchValue: newSearchValue }),
  searchContainerRef: { current: null },
}));

/**
 * Inits zustand from local storage. This should only be called in one place in the app.
 */
const useInitLatestSignupsVisited = () => {
  // value will always be the first this it was when the hook was mounted.
  const [value, setValue] = useLocalStorage("latestSignupsVisited", []);
  const activeSignups = useActiveSignups();
  const setLatestSignupsVisited = useSignupsAndEventsStore(
    (s) => s.setLatestSignupsVisited
  );
  const isInitRef = useSignupsAndEventsStore((s) => s.isInitRef);

  useEffect(() => {
    if (isInitRef.current) return;
    isInitRef.current = true;
    let finalValue = cloneDeep(value);

    // transform undefined and old data structures.
    if (!Array.isArray(finalValue)) finalValue = [];
    let currentAge = 0;
    finalValue = finalValue.map((item, idx) => {
      if (typeof item === "number") {
        return { age: idx, signupListID: item };
      }
      if (typeof item === "object") {
        return { age: idx, ...item };
      }
      return item;
    });

    currentAge += finalValue.length;

    if (finalValue.length < limit) {
      for (let idx = 0; idx < activeSignups.length; idx++) {
        const signupID = activeSignups[idx].ID;
        // Check if this signup is already in the list to prevent duplicates
        if (!finalValue.find((item) => item.signupListID === signupID)) {
          finalValue.push({
            age: currentAge,
            signupListID: signupID,
          });
          currentAge++;
        }
        if (finalValue.length >= limit) break;
      }
    }
    setValue(finalValue);
    setLatestSignupsVisited(finalValue);
  }, [activeSignups, isInitRef, setLatestSignupsVisited, setValue, value]);
};

const assignAges = (signupsArray, idToFront) => {
  const sortedByAge = cloneDeep(signupsArray);
  const toFrontIdx = sortedByAge.findIndex(
    ({ signupListID }) => Number(signupListID) === Number(idToFront)
  );
  if (sortedByAge[toFrontIdx]) sortedByAge[toFrontIdx].age = -1;
  sortedByAge.sort(({ age: age1 }, { age: age2 }) => age1 - age2);
  const newSignupsArray = cloneDeep(signupsArray);

  const indexMap = Object.fromEntries(
    newSignupsArray.map(({ signupListID }, idx) => [signupListID, idx])
  );

  for (let idx = 0; idx < sortedByAge.length; idx++) {
    const newSignupsArrayIdx = indexMap[sortedByAge[idx].signupListID];
    newSignupsArray[newSignupsArrayIdx].age = idx;
  }

  return newSignupsArray;
};

const limit = 10;
const usePushLatestSignupsVisited = () => {
  const latestSignupsVisited = useSignupsAndEventsStore(
    (s) => s.latestSignupsVisited
  );
  const setLatestSignupsVisited = useSignupsAndEventsStore(
    (s) => s.setLatestSignupsVisited
  );
  const [, setValue] = useLocalStorage("latestSignupsVisited", []);

  return useCallback(
    (signupListID) => {
      if (!signupListID) return;

      if (isNaN(signupListID)) return;
      const newSignup = { age: 0, signupListID: Number(signupListID) };
      const newVisitedList = cloneDeep(latestSignupsVisited);
      if (
        !latestSignupsVisited?.find(
          ({ signupListID: ID }) => ID === newSignup.signupListID
        )
      ) {
        // replace the last one with the new one and place it first
        const oldestIdx = newVisitedList.findIndex(
          ({ age }) => age >= limit - 1
        );
        newVisitedList.splice(
          oldestIdx === -1 ? newVisitedList.length : oldestIdx, // since we're backfilling browser storge, adding to the end is just a fallback.
          1
        );
        newVisitedList.unshift(newSignup);
      }

      const agesAssigned = assignAges(newVisitedList, signupListID);

      if (!isEqual(agesAssigned, latestSignupsVisited)) {
        setLatestSignupsVisited(agesAssigned);
        setValue(agesAssigned);
      }
    },
    [latestSignupsVisited, setLatestSignupsVisited, setValue]
  );
};

const useSearchValue = () => {
  const searchValue = useSignupsAndEventsStore((s) => s.searchValue);
  const setSearchValue = useSignupsAndEventsStore((s) => s.setSearchValue);

  return [searchValue, setSearchValue];
};

const useDebouncedSearchValue = () => {
  const [searchValue] = useSearchValue();
  return useDebounce(searchValue, 230);
};

const useSignupsToDisplay = () => {
  const activeSignups = useActiveSignups();
  const latestSignupsVisited = useSignupsAndEventsStore(
    (s) => s.latestSignupsVisited
  );

  const latestVisitedActiveSignups = useMemo(() => {
    const mLatestVisitedActiveSignups = [];
    for (let idx = 0; idx < latestSignupsVisited.length; idx++) {
      let visitedID = latestSignupsVisited[idx].signupListID;

      const signupToAdd = activeSignups.find(
        ({ ID: currentID }) => currentID === visitedID
      );
      // if the signup has been archived, it will not be added.
      if (signupToAdd) mLatestVisitedActiveSignups.push(signupToAdd);
      if (mLatestVisitedActiveSignups.length >= limit)
        return mLatestVisitedActiveSignups; // This should always happen
    }

    // Since we're now backfilling local storage, this code should never happen.
    // But if something unexpected happened, this would be good to fall back on.
    for (let idx = 0; idx < activeSignups.length; idx++) {
      if (
        !mLatestVisitedActiveSignups.find(
          ({ ID }) => ID === activeSignups[idx].ID
        )
      )
        mLatestVisitedActiveSignups.push(activeSignups[idx]);
      if (mLatestVisitedActiveSignups.length >= limit)
        return mLatestVisitedActiveSignups;
    }

    return mLatestVisitedActiveSignups;
  }, [activeSignups, latestSignupsVisited]);

  const debouncedSearchValue = useDebouncedSearchValue();
  const [signupsToDisplay, setSignupsToDisplay] = useState(
    latestVisitedActiveSignups
  );

  // In addition to using a debounced search value, we utilize a Promise-based
  // effect to keep the search input responsive and the result list updates smooth
  useEffect(() => {
    let shouldIgnore = false;

    if (debouncedSearchValue.length === 0) {
      setSignupsToDisplay(latestVisitedActiveSignups);
    } else {
      Promise.all(
        activeSignups.map((signup) =>
          Promise.resolve(
            typeof signup?.title === "string" &&
              signup.title
                .toLowerCase()
                .includes(debouncedSearchValue.toLowerCase())
          )
        )
      ).then((areIncluded) => {
        if (!shouldIgnore) {
          setSignupsToDisplay(activeSignups.filter((_, i) => areIncluded[i]));
        }
      });
    }

    return () => {
      shouldIgnore = true;
    };
  }, [activeSignups, debouncedSearchValue, latestVisitedActiveSignups]);

  return signupsToDisplay;
};

const useSearchContainerRef = () =>
  useSignupsAndEventsStore((s) => s.searchContainerRef);

const useClearSearch = () => {
  const setSearchValue = useSignupsAndEventsStore((s) => s.setSearchValue);

  return useCallback(() => {
    setSearchValue("");
  }, [setSearchValue]);
};

export {
  useInitLatestSignupsVisited,
  usePushLatestSignupsVisited,
  useSignupsToDisplay,
  useSearchValue,
  useDebouncedSearchValue,
  useSearchContainerRef,
  useClearSearch,
};
